/**
 * Form Handler
 *
 * Handles AJAX form submissions for login, register, and password reset.
 */

export default class Forms {
    constructor() {
        this.forms = {};
        this.config = window.wdLoginPopup || {};

        this.init();
    }

    /**
     * Initialize form handlers.
     */
    init() {
        this.forms = {
            login: document.querySelector('.wd-lp-form--login'),
            register: document.querySelector('.wd-lp-form--register'),
            password: document.querySelector('.wd-lp-form--password'),
        };

        Object.entries(this.forms).forEach(([type, form]) => {
            if (form) {
                form.addEventListener('submit', (e) => this.handleSubmit(e, type));
            }
        });

        this.initWooCommerceAccountLogin();

        // Initialize password strength meter if available
        this.initPasswordStrength();
    }

    /**
     * Use the popup JSON endpoint for the login form displayed on WooCommerce's
     * My Account page as well. The legacy wd-js handler posts the whole page and
     * cannot display its errors on current themes because its notice target no
     * longer exists.
     */
    initWooCommerceAccountLogin() {
        const form = document.querySelector(
            'body.woocommerce-account form.woocommerce-form-login:not(.wd-lp-form--login)'
        );

        if (!form) {
            return;
        }

        // Capture the submit before the legacy jQuery Form listener can send a
        // second request for the same credentials.
        form.addEventListener('submit', (e) => {
            if (!this.config.ajaxEnabled) {
                return;
            }

            e.stopImmediatePropagation();
            this.handleSubmit(e, 'login', {
                accountForm: true,
                messageContainer: this.getWooCommerceMessageContainer(form),
            });
        }, true);
    }

    /**
     * Handle form submission.
     *
     * @param {Event} e - Submit event
     * @param {string} type - Form type (login, register, password)
     */
    async handleSubmit(e, type, options = {}) {
        // Check if AJAX is enabled
        if (!this.config.ajaxEnabled) {
            return; // Let form submit normally
        }

        e.preventDefault();

        const form = e.target;
        const button = form.querySelector('button[type="submit"]');
        const messageContainer = options.messageContainer || form.querySelector('.wd-lp-messages');

        // Show loading state
        this.setLoading(button, true);
        this.clearMessages(messageContainer);

        // Build form data
        const formData = new FormData(form);

        // Ensure action is set (may already be in hidden field)
        if (!formData.get('action')) {
            const actionMap = {
                login: 'wd_lp_auth',
                register: 'wd_um_popup_register',
                password: 'wd_um_popup_lost_password',
            };
            formData.append('action', actionMap[type]);
        }

        // Ensure security nonce is set
        if (!formData.get('security')) {
            formData.append('security', this.config.nonce);
        }

        // Determine endpoint: REST API for login, AJAX for others
        let endpoint = this.config.ajaxUrl;
        let useRest = false;

        if (type === 'login' && this.config.restUrl) {
            endpoint = this.config.restUrl;
            useRest = true;
        }

        // Debug: Log what we're sending
        console.log('[WD Login Popup] Submitting form:', {
            type,
            action: formData.get('action'),
            security: formData.get('security') ? 'present' : 'missing',
            endpoint: endpoint,
            useRest: useRest,
        });

        try {
            let response = await fetch(endpoint, {
                method: 'POST',
                body: formData,
                credentials: 'same-origin',
            });

            // If REST endpoint failed (404/500), fallback to AJAX
            if (useRest && !response.ok && response.status >= 400) {
                console.log('[WD Login Popup] REST failed, falling back to AJAX');
                endpoint = this.config.ajaxUrl;
                response = await fetch(endpoint, {
                    method: 'POST',
                    body: formData,
                    credentials: 'same-origin',
                });
            }

            const data = await response.json();

            // Check if response is in expected WordPress JSON format
            if (typeof data.success === 'undefined') {
                // Unexpected response format - likely intercepted by another handler
                console.error('Login popup: Unexpected response format', data);
                console.error('Expected WordPress JSON format with "success" field');
                console.error('FormData action:', formData.get('action'));

                this.showSubmissionMessage(
                    messageContainer,
                    'Erreur de communication avec le serveur. Veuillez réessayer.',
                    'error',
                    options.accountForm
                );
                this.resetRecaptcha();
                return;
            }

            if (data.success) {
                this.showSubmissionMessage(
                    messageContainer,
                    data.data?.message || 'Succès!',
                    'success',
                    options.accountForm
                );

                // Handle redirect
                if (data.data?.redirect) {
                    setTimeout(() => {
                        window.location.href = data.data.redirect;
                    }, 500);
                } else if (type === 'login') {
                    // No redirect specified - refresh the page (default behavior)
                    setTimeout(() => {
                        window.location.reload();
                    }, 500);
                } else if (type === 'password') {
                    // For password reset, just show success and maybe close modal
                    setTimeout(() => {
                        if (window.wdLoginPopupModal) {
                            window.wdLoginPopupModal.showSection('login');
                        }
                    }, 2000);
                }

                // Dispatch success event
                form.dispatchEvent(new CustomEvent('wdLoginPopupSuccess', {
                    detail: { type, data: data.data }
                }));
            } else {
                const errorMessage = data.data?.message || 'Une erreur est survenue.';
                this.showSubmissionMessage(
                    messageContainer,
                    errorMessage,
                    'error',
                    options.accountForm
                );

                // Handle field-specific errors
                if (data.data?.errors) {
                    this.highlightErrors(form, data.data.errors);
                }

                // Reset reCAPTCHA on error
                this.resetRecaptcha();

                // Dispatch error event
                form.dispatchEvent(new CustomEvent('wdLoginPopupError', {
                    detail: { type, error: data.data }
                }));
            }
        } catch (error) {
            console.error('Login popup error:', error);

            if (options.accountForm) {
                this.submitWooCommerceFallback(form, button);
                return;
            }

            this.showMessage(
                messageContainer,
                this.config.i18n?.error || 'Une erreur est survenue.',
                'error'
            );
            this.resetRecaptcha();
        } finally {
            this.setLoading(button, false);
        }
    }

    /**
     * Return the standard WooCommerce notice area, creating one if the active
     * theme omitted it.
     *
     * @param {HTMLFormElement} form
     * @returns {HTMLElement}
     */
    getWooCommerceMessageContainer(form) {
        const wrapper = form.closest('.woocommerce') || form.parentElement;
        let container = wrapper?.querySelector('.woocommerce-notices-wrapper');

        if (!container) {
            container = document.createElement('div');
            container.className = 'woocommerce-notices-wrapper';
            form.parentNode.insertBefore(container, form);
        }

        container.setAttribute('role', 'status');
        container.setAttribute('aria-live', 'polite');

        return container;
    }

    /**
     * Preserve WooCommerce's normal POST login if the JSON request cannot be
     * completed. Native form.submit() bypasses both AJAX submit listeners.
     *
     * @param {HTMLFormElement} form
     * @param {HTMLButtonElement|null} button
     */
    submitWooCommerceFallback(form, button) {
        let loginField = form.querySelector('input[type="hidden"][name="login"]');

        if (!loginField) {
            loginField = document.createElement('input');
            loginField.type = 'hidden';
            loginField.name = 'login';
            form.appendChild(loginField);
        }

        loginField.value = button?.value || button?.textContent?.trim() || 'Login';
        HTMLFormElement.prototype.submit.call(form);
    }

    /**
     * Render feedback using the styles appropriate to the submitted form.
     *
     * @param {HTMLElement} container
     * @param {string} message
     * @param {string} type
     * @param {boolean} accountForm
     */
    showSubmissionMessage(container, message, type, accountForm = false) {
        if (!accountForm) {
            this.showMessage(container, message, type);
            return;
        }

        if (!container) {
            return;
        }

        const cssClass = type === 'success' ? 'woocommerce-message' : 'woocommerce-error';
        const role = type === 'success' ? 'status' : 'alert';

        container.innerHTML = `
            <div class="${cssClass}" role="${role}">
                ${this.escapeHtml(message)}
            </div>
        `;

        container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    /**
     * Set button loading state.
     *
     * @param {HTMLButtonElement} button
     * @param {boolean} loading
     */
    setLoading(button, loading) {
        if (!button) return;

        button.disabled = loading;
        button.classList.toggle('wd-lp-btn--loading', loading);
    }

    /**
     * Show a message in the container.
     *
     * @param {HTMLElement} container
     * @param {string} message
     * @param {string} type - 'success' or 'error'
     */
    showMessage(container, message, type) {
        if (!container) return;

        container.innerHTML = `
            <div class="wd-lp-message wd-lp-message--${type}">
                ${this.escapeHtml(message)}
            </div>
        `;

        // Auto-hide success messages after 4 seconds
        if (type === 'success') {
            setTimeout(() => {
                this.clearMessages(container);
            }, 4000);
        }

        // Scroll message into view
        container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    /**
     * Clear messages from container.
     *
     * @param {HTMLElement} container
     */
    clearMessages(container) {
        if (container) {
            container.innerHTML = '';
        }
    }

    /**
     * Highlight fields with errors.
     *
     * @param {HTMLFormElement} form
     * @param {Object} errors - Field name to error message map
     */
    highlightErrors(form, errors) {
        // Clear previous error states
        form.querySelectorAll('.wd-lp-field--error').forEach(field => {
            field.classList.remove('wd-lp-field--error');
        });

        // Add error states to fields
        Object.keys(errors).forEach(fieldName => {
            const input = form.querySelector(`[name="${fieldName}"]`);
            if (input) {
                const field = input.closest('.wd-lp-field');
                if (field) {
                    field.classList.add('wd-lp-field--error');
                }
            }
        });
    }

    /**
     * Reset reCAPTCHA widget.
     */
    resetRecaptcha() {
        if (typeof grecaptcha !== 'undefined') {
            try {
                grecaptcha.reset();
            } catch (e) {
                // Ignore if no widget
            }
        }
    }

    /**
     * Initialize password strength meter.
     */
    initPasswordStrength() {
        const passwordFields = document.querySelectorAll('.wd-lp-form--register input[type="password"]');

        passwordFields.forEach(field => {
            if (field.name === 'password' && typeof wp !== 'undefined' && wp.passwordStrength) {
                field.addEventListener('input', () => {
                    const strengthMeter = field.closest('.wd-lp-field')?.querySelector('.wd-lp-password-strength');
                    if (strengthMeter) {
                        const strength = wp.passwordStrength.meter(field.value, [], '');
                        const classes = ['', 'short', 'bad', 'good', 'strong'];
                        const labels = ['', 'Très faible', 'Faible', 'Bon', 'Fort'];

                        strengthMeter.className = `wd-lp-password-strength woocommerce-password-strength ${classes[strength] || ''}`;
                        strengthMeter.textContent = labels[strength] || '';
                    }
                });
            }
        });
    }

    /**
     * Escape HTML to prevent XSS.
     *
     * @param {string} str
     * @returns {string}
     */
    escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }
}
